import { Box, Button, Chip, FormControl, Stack, Tab, Typography } from "@mui/material";
import React from "react";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import Panel, { PanelProps } from "../Panel/panel";
import PanelTabs from "../PanelTabs";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DbShareMediaInfos } from "../../../Core/Api/Types/db-share-media-infos";
import FormSection from "../../Forms/FormSection/form-section";
import LanguageIcon from "@mui/icons-material/Language";
import SettingsIcon from "@mui/icons-material/Settings";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import TableCellShareStatus from "../../Table/TableCellShareStatus/table-cell-share-status";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import ClipCard from "../../Cards/ClipCard/clip-card";
import moment from "moment";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ClipContext } from "../../../Core/Api/Types/clip-context";
import { useSelection } from "../../../Utils/Hooks/useSelection";
import { Clip } from "../../../Core/Api/Types/clip";
import { ImportClipsRequest } from "../../../Core/Api/Types/import-clips-request";
import { cacheService, logService, shareService } from "../../../Core/Api/services";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ActivityMappedData } from "../../Activity/activity";
import { routePaths } from "../../../Router/router";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import { AclMatch, OpenLinkInNewTab, UserHasAcls } from "../../../Utils/helpers";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TableCellBool from "../../Table/TableCellBool";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { useTranslation } from "react-i18next";

export interface ShareDetailsPanelProps extends PanelProps {
  share: DbShareMediaInfos | undefined;
  openEditModal?: () => void;
  openMetricsModal?: () => void;
  deleteShare?: () => void;
}

const ShareDetailsPanel: React.FC<ShareDetailsPanelProps> = ({
  open,
  onClose,
  openEditModal,
  openMetricsModal,
  deleteShare,
  share,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = React.useState(0);
  const [activities, setActivities] = React.useState<ActivityMappedData[]>([]);
  const { isSelected: isExpanded, select: expand } = useSelection("0");
  const customSnackbar = useCustomSnackbar();

  console.log("share..", share);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 2) getActivities(share?.objectId);
    setTabValue(newValue);
  };

  const handleImportClip = (clip: Clip) => {
    let request = new ImportClipsRequest({
      shareId: share?.id,
      clips: [clip],
    });

    shareService.ImportClips(request).then(() => {
      customSnackbar(t("alert:success.clipIsImported"), "success");
    });
  };

  const getActivities = (assetId) => {
    logService.ListResourceActivities(ActivityKind.Asset, assetId, 1, 20).then((res) => {
      setActivities(res);
    });
  };

  return (
    <Panel
      reload={share?.id}
      header={<PanelHeader title={t("panel:title.shareDetails")} onClose={onClose as PanelHeaderProps["onClose"]} />}
      footerContent={
        <Stack direction="row" alignItems="center" gap={1} flexWrap={"wrap"} flex={"1 1 100%"}>
          <Button
            sx={{ flex: "1 1 25%" }}
            startIcon={<DeleteOutlinedIcon />}
            color="error"
            onClick={deleteShare}
            disabled={!deleteShare}
          >
            {t("common:delete")}
          </Button>
          <Button
            sx={{ flex: "1 1 25%" }}
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => OpenLinkInNewTab(routePaths.viewLink.path + "/" + share?.key)}
          >
            {t("common:preview")}
          </Button>
          {UserHasAcls([AclTypes.aclAdmin, AclTypes.aclSuperUser, AclTypes.aclShareView], AclMatch.OneAtLeast) &&
            cacheService.GetPlatformSettings()?.wowzaUseHlsChunker && (
              <Button sx={{ flex: "1 1 25%" }} startIcon={<QueryStatsIcon />} onClick={openMetricsModal}>
                {t("common:metricsUpperCase")}
              </Button>
            )}
          <Button sx={{ flex: "1 1 25%" }} variant="contained" onClick={openEditModal} disabled={!openEditModal}>
            {t("common:editUpperCase")}
          </Button>
        </Stack>
      }
      open={open}
      onClose={onClose}
      {...otherProps}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack>
          {share?.objectId !== -500 && (
            <Typography variant="h6" color="text.tier" mb={0.5}>
              {share?.objectLayer} #{share?.objectId}
            </Typography>
          )}
          <Typography variant="h5" mb={1.5}>
            {share?.name}
          </Typography>
          {/* <AssetStatus status={ObjectStatus.Accepted} /> */}
          {share?.isActive !== null && (
            <TableCellShareStatus
              data={{
                value: share?.isActive,
                transcoding:
                  share?.linkedOrderItemStatus === OrderStatus.READY ||
                  share?.linkedOrderItemStatus === OrderStatus.QUEUED ||
                  share?.linkedOrderItemStatus === OrderStatus.RUNNING,
                nbViews: share?.viewInfos?.length ?? 0,
                nbTotalViews: share?.viewsMax,
              }}
            />
          )}
        </Stack>
        <AssetThumbnail size="large" type={ObjectType.Video} /*TODO : Was trailer => find new equivalent*/ />
      </Stack>
      <PanelTabs variant="fullWidth" value={tabValue} onChange={handleChange} sx={{ pr: 3, pl: 3 }}>
        <Tab label={t("common:settings")} {...a11yProps(0)} />
        <Tab label={t("common:clips")} {...a11yProps(1)} />
        <Tab label={t("common:activities")} {...a11yProps(2)} />
      </PanelTabs>
      <Box pt={3}>
        <PanelTabContent index={0} activePanelIndex={tabValue} id="share-details-panel">
          {share?.objectLayer !== ObjectLayer.Asset && (
            <FormSection
              defaultExpanded={false}
              title={t("common:localization")}
              icon={<LanguageIcon />}
              sx={{ mb: 2 }}
              status={"details"}
              statusText={
                <Stack direction="row" sx={{ flexWrap: "wrap" }} color="text.tier" spacing={1}>
                  {share?.audioLanguagesSettings?.length !== undefined && share?.audioLanguagesSettings?.length > 0 && (
                    <Stack direction="row">
                      <GraphicEqOutlinedIcon fontSize="medium" sx={{ mt: -0.1, mr: 0.5 }} />
                      <Typography>{share?.audioLanguagesSettings?.map((el) => el.value).join(" · ")}</Typography>
                    </Stack>
                  )}
                  {share?.subtitleLanguagesSettings?.length !== undefined &&
                    share?.subtitleLanguagesSettings?.length > 0 && (
                      <Stack direction="row">
                        <ClosedCaptionOutlinedIcon sx={{ mt: -0.1, mr: 0.5 }} />
                        <Typography>{share?.subtitleLanguagesSettings?.map((el) => el.value).join(" · ")}</Typography>
                      </Stack>
                    )}
                </Stack>
              }
            >
              <FormControl>
                <MetadataListTextItem title={t("field:label.audioLanguage")}>
                  <Stack direction="row" spacing={1}>
                    {share?.audioLanguagesSettings?.length !== undefined &&
                    share?.audioLanguagesSettings?.length > 0 ? (
                      share?.audioLanguagesSettings?.map((lang) => (
                        <Chip key={lang.value} label={lang.value} sx={{ mb: 1 }} />
                      ))
                    ) : (
                      <Chip key={"-"} label={"-"} sx={{ mb: 1 }} />
                    )}
                  </Stack>
                </MetadataListTextItem>

                <MetadataListTextItem title={t("field:label.subtitleLanguage")}>
                  <Stack direction="row" spacing={1}>
                    {share?.subtitleLanguagesSettings?.length !== undefined &&
                    share?.subtitleLanguagesSettings?.length > 0 ? (
                      share?.subtitleLanguagesSettings?.map((lang) => (
                        <Chip key={lang.value} label={lang.value} sx={{ mb: 1 }} />
                      ))
                    ) : (
                      <Chip key={"-"} label={"-"} sx={{ mb: 1 }} />
                    )}
                  </Stack>
                </MetadataListTextItem>
              </FormControl>
            </FormSection>
          )}

          <FormSection
            defaultExpanded={false}
            title={t("form:section.title.shareSettings")}
            icon={<SettingsIcon />}
            sx={{ mb: 2 }}
            status={"details"}
            statusText={
              <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                <Typography>
                  {share?.comment}
                  {share?.comment && share?.comment.length > 0 && (
                    <FiberManualRecordIcon sx={{ width: 7, height: 7, ml: 0.7, mr: 0.7 }} />
                  )}
                </Typography>
                <Typography>
                  {share?.daysUntilExpire} {t("common:days")}
                </Typography>
              </Stack>
            }
          >
            <FormControl sx={{ mb: 1 }}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("field:label.shareName")}>
                  <Typography>{share?.name}</Typography>
                </MetadataListTextItem>
                <MetadataListTextItem title={t("field:label.expiresOn")}>
                  <Typography>{moment(share?.endDate!).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}</Typography>
                </MetadataListTextItem>

                <MetadataListTextItem title={t("common:comment")}>
                  <Typography>{share?.comment ?? "-"}</Typography>
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.isMultiple")}>
                  <TableCellBool value={share?.isMultiple ?? false} />
                </MetadataListTextItem>
                {share?.linkedOrderId && (
                  <MetadataListTextItem title={t("menu:title.orderId")}>
                    <Typography>{share?.linkedOrderId}</Typography>
                  </MetadataListTextItem>
                )}
              </Stack>
            </FormControl>
          </FormSection>

          <FormSection
            defaultExpanded={false}
            title={t("common:security")}
            icon={<SecurityIcon />}
            sx={{ mb: 2 }}
            status={"details"}
            statusText={
              <Stack direction="row" sx={{ flexWrap: "wrap" }} color="text.tier">
                {share?.ipsMax && (
                  <Stack>
                    <Typography sx={{ direction: "row" }}>
                      {share?.ipsMax + " IPs"}
                      <FiberManualRecordIcon sx={{ width: 7, height: 7, ml: 0.7, mr: 0.7 }} />
                    </Typography>
                  </Stack>
                )}
                {share?.clipsMaxNumber && (
                  <Typography>
                    {t("panel:body.clips", { count: share?.clipsMaxNumber })}
                    <FiberManualRecordIcon sx={{ width: 7, height: 7, ml: 0.7, mr: 0.7 }} />
                  </Typography>
                )}
                {share?.clipsMaxDuration && (
                  <Typography>
                    {share?.clipsMaxDuration} <FiberManualRecordIcon sx={{ width: 7, height: 7, ml: 0.7, mr: 0.7 }} />
                  </Typography>
                )}
                {share?.watermarkTemplate && (
                  <Typography>
                    {share?.watermarkTemplate} <FiberManualRecordIcon sx={{ width: 7, height: 7, ml: 0.7, mr: 0.7 }} />
                  </Typography>
                )}
                {share?.watermarkText && <Typography>{share?.watermarkText}</Typography>}
              </Stack>
            }
          >
            <Stack spacing={1.5}>
              <MetadataListTextItem title={t("field:label.maxNumberOfIps")}>
                <Typography>{share?.ipsMax ? share?.ipsMax : "-"}</Typography>
              </MetadataListTextItem>
              <MetadataListTextItem title={t("field:label.maxDotNumberOfClips")}>
                <Typography>{share?.clipsMaxNumber ? share?.clipsMaxNumber : "-"}</Typography>
              </MetadataListTextItem>
              <MetadataListTextItem title={t("field:label.maxDurationOfClips")}>
                <Typography>{share?.clipsMaxDuration ? share?.clipsMaxDuration : "-"}</Typography>
              </MetadataListTextItem>
              <MetadataListTextItem title={t("field:label.watermarkTemplate")}>
                <Typography>{share?.watermarkTemplate ? share?.watermarkTemplate : "-"}</Typography>
              </MetadataListTextItem>
              <MetadataListTextItem title={t("field:label.watermarkContent")}>
                <Typography>{share?.watermarkText ? share?.watermarkText : "-"}</Typography>
              </MetadataListTextItem>
            </Stack>
          </FormSection>

          <FormSection
            defaultExpanded={false}
            title={t("common:recipients")}
            icon={<PersonIcon />}
            sx={{ mb: 2 }}
            status={"details"}
            statusText={
              <Stack direction="row" sx={{ flexWrap: "wrap" }} color="text.tier" spacing={1}>
                {share?.recipientEmails && <Typography>{share?.recipientEmails[0]}</Typography>}
                {share?.recipientEmails?.length !== undefined && share?.recipientEmails.length > 1 && (
                  <Chip
                    key={share?.recipientEmails?.length - 1}
                    size="micro"
                    label={"+" + (share?.recipientEmails?.length - 1)}
                  />
                )}
              </Stack>
            }
          >
            {share?.recipientEmails?.map((email) => (
              <Chip key={email} label={email} sx={{ mb: 1 }} />
            ))}
          </FormSection>
        </PanelTabContent>
        <PanelTabContent index={1} activePanelIndex={tabValue} id="share-details-panel">
          {share?.clips?.length !== undefined && share!.clips?.length > 0 ? (
            <Stack spacing={1} mt={1.5}>
              {share?.clips?.map((clip) => (
                <ClipCard
                  key={clip.id}
                  expandable
                  id={clip.id}
                  timecodes={{
                    in: {
                      markFrame: clip.frameIn!,
                      isSet: true,
                    },
                    out: {
                      markFrame: clip.frameOut!,
                      isSet: true,
                    },
                  }}
                  timecodeIn={clip.timecodeIn ?? undefined}
                  timecodeOut={clip.timecodeOut ?? undefined}
                  importedOnAssetId={clip.importedOnAssetId !== null}
                  clipName={clip.comment}
                  context={ClipContext.Share}
                  createdBy={clip.userName}
                  createdAt={moment(clip.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                  isExpanded={isExpanded(clip.id.toString())}
                  onClickExpand={() => expand(clip.id.toString())}
                  onClickUpload={() => handleImportClip(clip)}
                />
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">{t("panel:sharePanel.body.noClipsFound")}</Typography>
          )}
        </PanelTabContent>

        <PanelTabContent index={2} activePanelIndex={tabValue} id="share-details-panel">
          <Typography color="text.secondary">{t("panel:sharePanel.body.noActivitiesFound")}</Typography>
        </PanelTabContent>
      </Box>
    </Panel>
  );
};

export default ShareDetailsPanel;

function a11yProps(index: number) {
  return {
    id: `share-details-panel-tab-${index}`,
    "aria-controls": `share-details-panel-tabpanel-${index}`,
  };
}
